import ShortId from "short-unique-id";

import Firebase from "@/services/Firebase";

function lastValue(nextValue, changePerc) {
  return nextValue / (1 + changePerc / 100.0);
}

function findAsset(assets, findParams) {
  const index = assets.findIndex((v) => {
    return (
      v.exchId === findParams.exchId && v.currencyId === findParams.currencyId
    );
  });
  if (findParams.returnIndex) {
    return index;
  }
  return index > -1 ? assets[index] : null;
}
function prepareAsset(asset) {
  return {
    exchId: asset.exchId || 0,
    currencyId: asset.currencyId,
    color: asset.color || null,
    notes: asset.notes || null,
  };
}
function prepareAssets(assets) {
  return assets.map(prepareAsset);
}

function prepareTransaction(transaction) {
  return {
    id: transaction.id,
    parent: transaction.parent || null,
    currencyId: transaction.currencyId,
    exchId: transaction.exchId || 0,
    holdings: transaction.holdings,
    assetPrice: transaction.assetPrice, // in assetPriceCurrency
    assetPriceCurrency: transaction.assetPriceCurrency || "USD", // currency
    sum: transaction.sum, // TODO: do we need this?
    date: transaction.date,
    notes: transaction.notes || null,
    showInValuation:
      transaction.showInValuation !== undefined
        ? transaction.showInValuation
        : true,
  };
}
function prepareTransactions(transactions) {
  return transactions
    .map(prepareTransaction)
    .filter((asset) => asset.exchId === 0) // NOTE: not pass other exchanges except Cointrackr
  ; 
}

export default {
  namespaced: true,
  state: {
    showHistory: false,
    assets: [],
    assetsLocal: [],
    transactions: [],
    transactionsLocal: [],
  },
  getters: {
    showHistory: (state) => state.showHistory,

    // used for color and notes store only
    assets: (state) => state.assets,
    assetsWithColors: (_state, getters, _rootState, rootGetters) => {
      return getters.assetsIds
        .map((currencyId) => {
          const portfolioAsset = rootGetters["portfolio/assetSimpleFind"]({
            currencyId,
          });
          let plAsset = getters.getAsset(currencyId);
          if (!portfolioAsset && !plAsset) {
            return null;
          }
          let asset = {};
          if (!plAsset) {
            asset = {
              currencyId,
              exchId: 0, // TODO: from where??
            };
          } else {
            asset = { ...plAsset };
          }
          if (!asset.color && portfolioAsset && portfolioAsset.color) {
            asset.color = portfolioAsset.color;
            asset.colorIsFromPortfolio = true;
          }
          return asset;
        })
        .filter((v) => v);
    },

    transactions: (state) => state.transactions,
    transactionsFilled: (state, getters, _rootState, rootGetters) => {
      // here every money related number is in USD
      const findCoin = rootGetters["coinmarketcap/coinByFindParams"];
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      const transactionsFilled = [];
      state.transactions.forEach((transaction) => {
        const coinData = findCoin({ currencyId: transaction.currencyId });
        if (!coinData) {
          return;
        }
        const plAsset = getters.getAssetWithColor(transaction.currencyId);
        let temp = { ...transaction };
        temp.coinData = coinData;
        temp.plAsset = plAsset;
        temp.holdingsLeft = temp.holdings;
        temp.showInValuation =
          temp.showInValuation !== undefined ? temp.showInValuation : true;
        temp.assetPrice_usd = convertToCurrency(
          temp.assetPrice,
          "USD",
          temp.assetPriceCurrency || "USD"
        );
        if (temp.parent) {
          temp.holdingsLeft = -1 * temp.holdings;
          temp.parentTransaction = getters.getTransactionSimple(temp.parent);
          temp.parentAssetPrice_usd = convertToCurrency(
            temp.parentTransaction.assetPrice,
            "USD",
            temp.parentTransaction.assetPriceCurrency || "USD"
          );
          temp.pl =
            temp.holdingsLeft * temp.assetPrice_usd -
            temp.holdingsLeft * temp.parentAssetPrice_usd;
          temp.valuation = temp.pl; // TODO: for top chart etc
          temp.valuationCurrent = temp.holdingsLeft * coinData.price_usd;
          // for transactions table sorting
          temp.buy_price = temp.parentAssetPrice_usd;
          temp.buy_value = temp.holdingsLeft * temp.parentAssetPrice_usd;
          temp.sell_value = temp.holdingsLeft * temp.assetPrice_usd;
          transactionsFilled.push(temp);
          return;
        }
        const children = state.transactions.filter((v) => v.parent === temp.id);
        children.forEach((child) => {
          temp.holdingsLeft += child.holdings;
        });
        temp.children = children;
        temp.valuation = temp.holdingsLeft * coinData.price_usd;
        temp.valuationCurrent = temp.valuation;
        temp.pl =
          temp.holdingsLeft * coinData.price_usd -
          temp.holdingsLeft * temp.assetPrice_usd;
        // for transactions table sorting
        temp.buy_value = temp.holdingsLeft * temp.assetPrice_usd;
        transactionsFilled.push(temp);
      });
      return transactionsFilled;
    },
    transactionsFilledInCurrency: (
      _state,
      getters,
      _rootState,
      rootGetters
    ) => {
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      // from USD to current currency conversion
      return getters.transactionsFilled.map((transaction) => {
        return {
          ...transaction,
          valuation: convertToCurrency(transaction.valuation),
          assetPrice_currency: convertToCurrency(transaction.assetPrice_usd),
          pl: convertToCurrency(transaction.pl),
          buy_price: convertToCurrency(transaction.buy_price || 0),
          buy_value: convertToCurrency(transaction.buy_value || 0),
          sell_value: convertToCurrency(transaction.sell_value || 0),
        };
      });
    },
    transactionsFilledForValuationInUSD: (state, getters) => {
      return getters.transactionsFilled.filter(
        (v) =>
          (v.showInValuation || v.showInValuation === undefined) &&
          (state.showHistory
            ? v.parent || !v.holdingsLeft
            : !v.parent && v.holdingsLeft)
      );
    },
    transactionsFilledForValuation: (state, getters) => {
      return getters.transactionsFilledInCurrency.filter((v) => {
        return (
          (v.showInValuation || v.showInValuation === undefined) &&
          (state.showHistory
            ? v.parent || !v.holdingsLeft
            : !v.parent && v.holdingsLeft)
        );
      });
    },

    // grouped transactions with assets info from CMC and assets
    assetsIds: (state) => {
      const assetsIds = [];
      state.transactions.forEach((v) => {
        if (assetsIds.indexOf(v.currencyId) < 0) {
          assetsIds.push(v.currencyId);
        }
      });
      return assetsIds;
    },
    assetsArr: (_state, getters, _rootState, rootGetters) => (
      history = false,
      transactions = null
    ) => {
      const assetsArr = [];
      if (!rootGetters["coinmarketcap/isReady"]) {
        return assetsArr;
      }
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      const assetsObj = {};
      const transactionsArr =
        transactions ||
        getters.transactionsFilledInCurrency.filter(
          (v) =>
            history === null ||
            (history
              ? v.parent || !v.holdingsLeft
              : !v.parent && v.holdingsLeft)
        );
      const profitlossEvaluation = getters.profitlossEvaluation;

      transactionsArr.forEach((v) => {
        if (!assetsObj[v.currencyId]) {
          const coinData = v.coinData;
          const plAsset = v.plAsset;
          assetsObj[v.currencyId] = {
            exchId: v.exchId,
            currencyId: v.currencyId,
            coinData: coinData,
            symbol: coinData.symbol,
            uniqueId: coinData.rid,
            rid: coinData.rid,
            name: coinData.name,
            price_usd: coinData.price_usd,
            price: convertToCurrency(coinData.price_usd),
            percChangeHour: coinData.percent_change_1h || 0,
            percChangeDay: coinData.percent_change_24h || 0,
            percChangeWeek: coinData.percent_change_7d || 0,
            transactions: [v],
            ...(plAsset
              ? {
                  notes: plAsset.notes,
                  color: plAsset.color,
                  colorIsFromPortfolio: plAsset.colorIsFromPortfolio || false,
                }
              : {
                  notes: null,
                  color: null,
                  colorIsFromPortfolio: false,
                }),
          };
        } else {
          assetsObj[v.currencyId].transactions = [
            ...assetsObj[v.currencyId].transactions,
            v,
          ];
        }
      });
      Object.entries(assetsObj).forEach(([currencyId, asset]) => {
        let holdingsLeft = 0;
        let sum_total = 0;
        let assetPrice_avg = 0;
        let valuation = 0;
        let pl_realized = 0;
        let pl_unrealized = 0;
        let pl_total = 0;
        const transactionsRes = [...asset.transactions];
        transactionsRes.sort((a, b) => (a.date > b.date ? -1 : 1));
        transactionsRes.forEach((transaction) => {
          holdingsLeft += transaction.holdingsLeft;
          sum_total +=
            transaction.holdingsLeft * transaction.assetPrice_currency;
          assetPrice_avg += transaction.assetPrice_currency;
          valuation += transaction.valuation;
          pl_realized += 0;
          pl_unrealized += transaction.pl;
        });
        assetPrice_avg = assetPrice_avg / (transactionsRes.length || 1);
        pl_total = pl_realized + pl_unrealized;
        const share = (100 / profitlossEvaluation) * Math.abs(valuation);
        assetsArr.push({
          ...asset,
          transactions: transactionsRes,
          holdingsLeft,
          sum_total,
          assetPrice_avg,
          valuation,
          pl_realized,
          pl_unrealized,
          pl_total,
          share,
        });
      });

      // TODO: count every assetsObj value, profit, loss (by transaction?)
      return assetsArr;
    },

    // searchers
    getAsset: (_state, getters) => (currencyId) => {
      return getters.assets.find((v) => v.currencyId === currencyId);
    },
    getAssetWithColor: (_state, getters) => (currencyId) => {
      return getters.assetsWithColors.find((v) => v.currencyId === currencyId);
    },
    getTransactionSimple: (_state, getters) => (transactionId) => {
      return getters.transactions.find((v) => v.id === transactionId);
    },
    getTransaction: (_state, getters) => (transactionId) => {
      return getters.transactionsFilled.find((v) => v.id === transactionId);
    },

    // add distribution shares, used by chart?
    portfolio: (state, getters) => {
      return getters.assetsArr(
        state.showHistory,
        getters.transactionsFilledForValuation
      );
    },
    portfolioAssetsSortedByName: (_state, getters) =>
      getters.portfolio.slice().sort((a, b) => a.name.localeCompare(b.name)),
    portfolioAssetsSortedByShare: (_state, getters) =>
      getters.portfolio.slice().sort((a, b) => b.share - a.share),

    // valuation
    profitlossTotalPLInUSD: (_state, getters) => {
      return getters.transactionsFilledForValuationInUSD.reduce(
        (acc, cur) => acc + cur.pl,
        0
      );
    },
    profitlossTotalPL(_state, getters, _rootState, rootGetters) {
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      return convertToCurrency(getters.profitlossTotalPLInUSD);
    },
    profitlossTotalPLInBTC(_state, getters, _rootState, rootGetters) {
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      return convertToCurrency(getters.profitlossTotalPLInUSD, "BTC");
    },

    // valuation
    profitlossEvaluationInUSD: (_state, getters) => {
      return getters.transactionsFilledForValuationInUSD.reduce(
        (acc, cur) => acc + Math.abs(cur.valuation),
        0
      );
    },
    profitlossEvaluation(_state, getters, _rootState, rootGetters) {
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      return convertToCurrency(getters.profitlossEvaluationInUSD);
    },
    profitlossEvaluationInBTC(_state, getters, _rootState, rootGetters) {
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      return convertToCurrency(getters.profitlossEvaluationInUSD, "BTC");
    },

    // profits and losses
    totalHourAgo: (_state, getters) =>
      getters.transactionsFilledForValuation.reduce(
        (acc, cur) =>
          acc + lastValue(cur.valuation, cur.coinData.percent_change_1h),
        0
      ),
    total24HoursAgo: (_state, getters) =>
      getters.transactionsFilledForValuation.reduce(
        (acc, cur) =>
          acc + lastValue(cur.valuation, cur.coinData.percent_change_24h),
        0
      ),
    total7DaysAgo: (_state, getters) =>
      getters.transactionsFilledForValuation.reduce(
        (acc, cur) =>
          acc + lastValue(cur.valuation, cur.coinData.percent_change_7d),
        0
      ),

    changeFromHourAgo: (_state, getters) =>
      getters.profitlossEvaluation - getters.totalHourAgo,
    changeFrom24HoursAgo: (_state, getters) =>
      getters.profitlossEvaluation - getters.total24HoursAgo,
    changeFrom7DaysAgo: (_state, getters) =>
      getters.profitlossEvaluation - getters.total7DaysAgo,

    changePercFromHourAgo: (_state, getters) =>
      getters.totalHourAgo
        ? (getters.profitlossEvaluation / getters.totalHourAgo - 1) * 100
        : 0,
    changePercFrom24HoursAgo: (_state, getters) =>
      getters.total24HoursAgo
        ? (getters.profitlossEvaluation / getters.total24HoursAgo - 1) * 100
        : 0,
    changePercFrom7DaysAgo: (_state, getters) =>
      getters.total7DaysAgo
        ? (getters.profitlossEvaluation / getters.total7DaysAgo - 1) * 100
        : 0,
  },
  mutations: {
    setAssets(state, assets) {
      state.assets = assets;
    },
    setAssetsLocal(state, assetsLocal) {
      state.assetsLocal = assetsLocal;
    },
    setTransactions(state, transactions) {
      state.transactions = transactions;
    },
    setTransactionsLocal(state, transactionsLocal) {
      state.transactionsLocal = transactionsLocal;
    },
    toggleShowHistory(state, value) {
      if (typeof value === "boolean") {
        state.showHistory = value;
      } else {
        state.showHistory = !state.showHistory;
      }
    },
  },
  actions: {
    setAssets: (
      { state, commit, dispatch, rootGetters },
      { assets, source }
    ) => {
      // remembering assetsLocal if it's first set from firebase
      if (
        !state.assetsLocal.length &&
        rootGetters["auth/isLoggedIn"] &&
        source === "firebase"
      ) {
        dispatch("setAssetsLocal", state.assets);
      }

      // TODO: firebase profitloss.assets
      // TODO: state.assets or [] (empty array?)
      // null can come only from Firebase startFirebaseSync once after register
      const assetsNew = prepareAssets(assets === null ? [] : assets);
      if (rootGetters["auth/isLoggedIn"] && source !== "firebase") {
        Firebase.saveProfitlossAssets(assetsNew);
      } else {
        commit("setAssets", assetsNew);
      }
    },
    addAsset: ({ state, dispatch }, asset) => {
      const assets = [...state.assets].concat([asset]);
      dispatch("setAssets", { assets });
    },
    updateAsset: ({ state, dispatch }, updateData) => {
      const index = findAsset(state.assets, {
        ...updateData,
        returnIndex: true,
      });
      if (index > -1) {
        const assets = [...state.assets];
        assets.splice(index, 1, {
          ...assets[index],
          ...updateData,
        });
        dispatch("setAssets", { assets });
      } else {
        dispatch("addAsset", updateData);
      }
    },
    setAssetsLocal: ({ commit }, assetsLocal) => {
      commit("setAssetsLocal", prepareAssets(assetsLocal));
    },

    setTransactions: (
      { commit, state, dispatch, rootGetters },
      { transactions, source }
    ) => {
      // remembering transactionsLocal if it's first set from firebase
      if (
        !state.transactionsLocal.length &&
        rootGetters["auth/isLoggedIn"] &&
        source === "firebase"
      ) {
        dispatch("setTransactionsLocal", state.transactions);
      }

      // null can come only from Firebase startFirebaseSync once after register
      // TODO: state.transactions or [] (empty array?)
      const transactionsNew = prepareTransactions(
        transactions === null ? [] : transactions
      );
      if (rootGetters["auth/isLoggedIn"] && source !== "firebase") {
        Firebase.saveProfitlossTransactions(transactionsNew);
      } else {
        commit("setTransactions", transactionsNew);
      }
    },
    setTransactionsLocal: ({ commit }, transactionsLocal) => {
      commit("setTransactionsLocal", prepareTransactions(transactionsLocal));
    },
    addTransaction: ({ state, dispatch }, transaction) => {
      const transactions = [...state.transactions].concat([transaction]);
      dispatch("setTransactions", { transactions });
    },
    updateTransaction: ({ state, dispatch }, updateData) => {
      const transactions = state.transactions.map((v) => {
        if (v.id === updateData.id) {
          return {
            ...v,
            ...updateData,
          };
        }
        return v;
      });
      dispatch("setTransactions", { transactions });
    },
    removeTransaction: ({ state, getters, dispatch }, transactionId) => {
      const transaction = getters.getTransaction(transactionId);
      if (
        !transaction.parent &&
        transaction.holdings !== transaction.holdingsLeft
      ) {
        dispatch("updateTransaction", {
          id: transactionId,
          holdings: transaction.holdings - transaction.holdingsLeft,
        });
      } else {
        const transactions = state.transactions.filter(
          (v) => v.id !== transactionId
        );
        dispatch("setTransactions", { transactions });
      }
    },
    removeAllTransactions: ({ dispatch }) => {
      dispatch("setTransactions", { transactions: [] });
    },

    setDefaultValues: ({ state, dispatch }) => {
      const assets = state.assetsLocal.length ? state.assetsLocal : [];
      const transactions = state.transactionsLocal.length
        ? state.transactionsLocal
        : [];
      dispatch("setAssets", { assets });
      dispatch("setTransactions", { transactions });
      dispatch("setAssetsLocal", []);
      dispatch("setTransactionsLocal", []);
    },

    toggleShowHistory: ({ commit }, value) => {
      commit("toggleShowHistory", value);
    },

    // SHAREable link
    async share({ getters, rootGetters }, message) {
      const showHistory = getters.showHistory;
      const id = new ShortId().randomUUID(13);
      const hash =
        Math.random()
          .toString(36)
          .substring(7) +
        (rootGetters["auth/userId"] || "") +
        Math.random()
          .toString(36)
          .substring(7);
      await Firebase.getShareLink(id, {
        created: Math.round(new Date().getTime() / 1000),
        message,
        hash,
        change: {
          hour: getters.changePercFromHourAgo,
          day: getters.changePercFrom24HoursAgo,
          week: getters.changePercFrom7DaysAgo,
        },
        assets: getters.portfolioAssetsSortedByShare.map((asset) => {
          const { currencyId, name, symbol, color, share } = asset;
          return { currencyId, name, symbol, color, share };
        }),
        mode: "pl-" + (showHistory ? "history" : "current"),
      });
      return id;
    },
    getSharedData(_context, id) {
      return Firebase.getSharedData(id);
    },
  },
};
